/**
 * Die E-Rechnungen — `EINV-001`, nach `07` §7.16 und `04` §4.10.
 *
 * **Warum ein eigener Bereich neben dem Eingangskorb.** Dort steht eine
 * Rechnung als *Vorgang*: eingegangen, zuzuordnen, erledigt. Hier steht sie
 * als *Rechnung*: Wer stellt sie, über wie viel, in welchem Profil, mit
 * welchem Befund. Das sind zwei Fragen, und die zweite stellt man, wenn der
 * Korb längst leer ist — etwa wenn der Steuerberater anruft.
 *
 * `07` §7.16 nennt die Felder, die die Bürokraft sieht: „Rechnungssteller,
 * Rechnungsnummer, Datum, Betrag, unterstütztes Profil und Validierungsstatus.
 * Abweichungen bleiben prüfbar; eine formal ungültige Rechnung wird nicht
 * still verworfen oder als gültig markiert. Das Original, sein Hash und das
 * Validierungsprotokoll bleiben zusammen auffindbar."
 *
 * Der letzte Satz ist der Grund für die Hashzeile an jeder Rechnung. Sie sieht
 * nach technischem Beiwerk aus und ist der Beleg: Wer nachweisen muss, dass
 * das archivierte Dokument unverändert ist, liest sie vor oder kopiert sie.
 *
 * **Kontierung, Zahlung und Ausgangsrechnung bleiben außerhalb** (`07` §7.12).
 * Diese Ansicht prüft und findet wieder; sie bucht nicht.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router";

import type { ERechnung, Rechnungsprofil } from "@hwc/demo-data";
import { findeAuftrag } from "@hwc/demo-data";
import { formatResourceRef } from "@hwc/contracts";

import { useSitzung } from "../../src/daten/Sitzung.js";
import {
  Leer,
  Liste,
  Zeile,
  datum,
  euro,
  zumAuftrag,
} from "../../src/bereiche/bausteine.js";

const PROFILTEXT: Record<Rechnungsprofil, string> = {
  "xrechnung-ubl": "XRechnung (UBL)",
  "xrechnung-cii": "XRechnung (CII)",
  "zugferd-en16931": "ZUGFeRD EN 16931",
  "zugferd-basic": "ZUGFeRD Basic",
  unbekannt: "unbekannt",
};

type Sicht = "alle" | "befund" | "offen";

export function ERechnungen() {
  const { bestand } = useSitzung();
  const [sicht, setzeSicht] = useState<Sicht>("alle");
  const [offen, setzeOffen] = useState<string | undefined>(undefined);

  const rechnungen = useMemo(() => {
    return bestand.rechnungen
      .filter((r) => {
        if (sicht === "befund") return r.validierung !== "gueltig";
        if (sicht === "offen") {
          const vorgang = bestand.eingang.find(
            (v) => formatResourceRef(v.ref) === formatResourceRef(r.eingangsvorgang),
          );
          return vorgang?.zugeordneterAuftrag === undefined;
        }
        return true;
      })
      .slice()
      .sort((a, b) => b.datum.localeCompare(a.datum));
  }, [bestand, sicht]);

  const summe = rechnungen.reduce((s, r) => s + r.betragCent, 0);

  return (
    <>
      <div className="bereichskopf">
        <h1>E-Rechnungen</h1>
        <span className="bereichskopf__zusatz">
          {rechnungen.length} · {euro(summe)}
        </span>
      </div>

      <div className="filter">
        {(
          [
            { wert: "alle", text: "Alle" },
            { wert: "befund", text: "Mit Befund" },
            { wert: "offen", text: "Nicht zugeordnet" },
          ] as const
        ).map((f) => (
          <button
            key={f.wert}
            type="button"
            className={`filter__knopf${sicht === f.wert ? " filter__knopf--aktiv" : ""}`}
            onClick={() => setzeSicht(f.wert)}
            aria-pressed={sicht === f.wert}
          >
            {f.text}
          </button>
        ))}
      </div>

      {rechnungen.length === 0 ? (
        <Leer
          text={
            sicht === "befund"
              ? "Keine Rechnung mit Befund. Das ist der Normalfall und keine Entwarnung."
              : "Keine Rechnung in dieser Sicht."
          }
        />
      ) : (
        <Liste>
          {rechnungen.map((r) => (
            <Zeile
              key={r.ref.opaqueId}
              titel={
                <button
                  type="button"
                  className="zeile__oeffnen"
                  onClick={() =>
                    setzeOffen(offen === r.ref.opaqueId ? undefined : r.ref.opaqueId)
                  }
                  aria-expanded={offen === r.ref.opaqueId}
                >
                  {r.steller}
                </button>
              }
              unterzeile={
                <>
                  {r.rechnungsnummer} · {datum(r.datum)} · {PROFILTEXT[r.profil]}
                </>
              }
              abzeichen={<Validierungsmarke rechnung={r} />}
              rechts={euro(r.betragCent)}
            />
          ))}
        </Liste>
      )}

      {offen && (
        <Rechnungsdetail
          rechnung={rechnungen.find((r) => r.ref.opaqueId === offen)!}
        />
      )}

      <p className="notiz">
        {/*
          Die Abgrenzung gehört sichtbar hierher. Wer eine Rechnungsliste
          sieht, nimmt sonst an, dass hier auch bezahlt wird.
        */}
        <strong>Kontierung, Zahlung und Ausgangsrechnung bleiben außerhalb</strong>{" "}
        (<code>07</code> §7.12). Dieser Bereich prüft und findet wieder; gebucht wird im
        ERP, und die Übergabe dorthin baut <code>EXP-001</code> in Phase 7.
      </p>
    </>
  );
}

/**
 * Die Validierung als Marke.
 *
 * Drei Ausgänge und nicht zwei — `07` §7.16 lässt eine ungültige Rechnung
 * weder still verwerfen noch als gültig markieren, und dazwischen liegt der
 * Fall, den ein boolesches Feld verschluckt: gültig, aber mit Anmerkungen.
 */
function Validierungsmarke({ rechnung }: { rechnung: ERechnung }) {
  if (rechnung.validierung === "gueltig") {
    return <span className="abzeichen abzeichen--erfolg">gültig</span>;
  }
  if (rechnung.validierung === "mit-anmerkungen") {
    return (
      <span className="abzeichen abzeichen--warnung">
        gültig, {rechnung.befunde.length} Anmerkung
        {rechnung.befunde.length === 1 ? "" : "en"}
      </span>
    );
  }
  return <span className="abzeichen abzeichen--fehler">ungültig</span>;
}

function Rechnungsdetail({ rechnung }: { rechnung: ERechnung }) {
  const { bestand } = useSitzung();

  const vorgang = bestand.eingang.find(
    (v) => formatResourceRef(v.ref) === formatResourceRef(rechnung.eingangsvorgang),
  );
  const auftrag = findeAuftrag(bestand, vorgang?.zugeordneterAuftrag);

  return (
    <section className="karte">
      <h2 style={{ marginTop: 0 }}>
        {rechnung.steller} · {rechnung.rechnungsnummer}
      </h2>

      <dl className="felder">
        <div className="feld">
          <dt className="feld__name">Datum</dt>
          <dd className="feld__wert">{datum(rechnung.datum)}</dd>
        </div>
        <div className="feld">
          <dt className="feld__name">Betrag</dt>
          <dd className="feld__wert">{euro(rechnung.betragCent)}</dd>
        </div>
        <div className="feld">
          <dt className="feld__name">Profil</dt>
          <dd className="feld__wert">{PROFILTEXT[rechnung.profil]}</dd>
        </div>
        <div className="feld">
          <dt className="feld__name">Zuordnung</dt>
          <dd className="feld__wert">
            {auftrag ? (
              <Link to={zumAuftrag(auftrag.nummer)}>Auftrag {auftrag.nummer}</Link>
            ) : (
              <Link to="/eingang">noch offen — im Eingangskorb</Link>
            )}
          </dd>
        </div>
      </dl>

      {rechnung.befunde.length > 0 && (
        <>
          <h3>Befunde</h3>
          {/*
            Vollständig, nicht gekürzt. Ein Befund, der mit „und 2 weitere"
            endet, ist einer, den niemand liest — und `07` §7.16 verlangt,
            dass Abweichungen prüfbar bleiben.
          */}
          <ul>
            {rechnung.befunde.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </>
      )}

      {rechnung.validierung === "ungueltig" && (
        <p className="notiz notiz--fehler">
          <strong>Nicht zur Zahlung vorgemerkt.</strong> Eine formal ungültige Rechnung
          wird nach <code>07</code> §7.16 weder still verworfen noch als gültig markiert
          — sie bleibt hier stehen, bis jemand sie klärt.
        </p>
      )}

      <h3>Original und Nachweis</h3>
      <p className="notiz">
        Das Original liegt unverändert in <strong>Paperless</strong>. Sein Hash und das
        Validierungsprotokoll bleiben mit ihm zusammen auffindbar (<code>07</code>{" "}
        §7.16):
        <br />
        <code style={{ userSelect: "all", wordBreak: "break-all" }}>
          {rechnung.originalHash}
        </code>
      </p>
    </section>
  );
}
